import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const ecommerceService = {
  name: "Ecommerce Development",
  promise: "Online stores optimized for sales and customer experience",
  primaryKPI: "32% average increase in conversion rates",
  signals: {
    bestFor: "Retailers ready to scale their online presence",
    timeline: "4-8 weeks from design to first sale",
    team: "Ecommerce specialist + developer + conversion optimizer",
    deliverables: "Custom store + payment setup + optimization"
  },
  outputs: [
    "Custom Shopify or WooCommerce store design",
    "Payment gateway integration (Stripe, PayPal, etc.)",
    "Inventory management system setup",
    "Conversion rate optimization implementation",
    "Mobile-first responsive design",
    "SEO and marketing tools integration"
  ],
  process: [
    {
      step: "Store Strategy & Planning",
      description: "Define product structure, payment methods, and customer journey",
      ownership: "We strategize, you provide product data",
      artifacts: ["Store architecture", "Payment setup plan", "Shipping strategy"]
    },
    {
      step: "Design & Development",
      description: "Create conversion-focused storefront with proven UX patterns",
      ownership: "We design and build, you approve designs",
      artifacts: ["Store design", "Product page templates", "Checkout flow"]
    },
    {
      step: "Product Setup & Testing",
      description: "Import products and test complete checkout process",
      ownership: "We configure, you verify product data",
      artifacts: ["Product catalog", "Shipping rules", "Test order reports"]
    },
    {
      step: "Launch & Optimization",
      description: "Go live with continuous conversion improvements",
      ownership: "We launch and optimize, you provide sales data",
      artifacts: ["Live store", "Analytics setup", "CRO recommendations"]
    }
  ],
  proof: [
    {
      client: "Fashion brand",
      challenge: "Struggling to scale beyond $10K/month revenue",
      result: "Optimized store with marketing integration and CRO",
      metric: "Grew from $10K to $100K/month in 6 months"
    },
    {
      client: "Specialty foods store",
      challenge: "Low average order values hurting profitability",
      result: "Implemented strategic upsells and product bundling",
      metric: "45% boost in average order value"
    },
    {
      client: "Electronics store",
      challenge: "High cart abandonment killing conversions",
      result: "Optimized checkout flow and added trust signals",
      metric: "58% reduction in cart abandonment"
    }
  ],
  faqs: [
    {
      question: "Which ecommerce platform should we use?",
      answer: "Shopify is ideal for most businesses - it's powerful, scalable, and handles hosting/security. WooCommerce works well if you need WordPress integration or custom functionality. For enterprise needs with complex requirements, we can build custom solutions using headless commerce APIs."
    },
    {
      question: "How do you handle payment security?",
      answer: "We integrate with PCI-compliant payment processors like Stripe and PayPal that handle sensitive card data. Your store never stores payment information directly, ensuring security and compliance. We also implement SSL certificates, fraud detection, and secure checkout flows."
    },
    {
      question: "Can you migrate our existing products?",
      answer: "Yes. We can migrate products, customers, and order history from your existing platform. We handle data mapping, image transfers, URL redirects, and SEO preservation to ensure a smooth transition with minimal disruption."
    },
    {
      question: "What about shipping integrations?",
      answer: "We integrate with major shipping carriers (USPS, FedEx, UPS, DHL) for real-time rates and label printing. We can also set up custom shipping rules, local delivery, pickup options, and international shipping with duty calculations."
    },
    {
      question: "Do you provide ongoing optimization?",
      answer: "Yes. E-commerce success requires continuous improvement. We offer optimization packages including A/B testing, conversion analysis, new feature implementation, seasonal design updates, and performance monitoring to grow your revenue over time."
    }
  ]
};

const Ecommerce = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Ecommerce Development - High-Converting Online Stores | AffinityAim"
        description="Ecommerce development with 28% avg revenue increase. Shopify, WooCommerce, custom platforms. Payment integration & optimization."
        canonical="https://www.affinityaim.com/services/ecommerce"
        keywords="ecommerce development, online store development, Shopify development, WooCommerce"
        type="service"
      />
      <Navigation />
      <main className="pt-32">
        <ServiceDetailPage service={ecommerceService} />
      </main>
      <Footer />
    </div>
  );
};

export default Ecommerce;
